import { useContext, useEffect } from "react";
import { ContextAuth } from "../../../contexts/ContextAuth";
import { savePayment, removePayment } from "../../../apis/apis.accounting/apisCash";
import { toast } from "react-toastify";

const paymentTypeBadge = (type) => {
    if (type === "DY") return "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300";
    if (type === "DC") return "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300";
    if (type === "HC") return "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300";
    return "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300";
};

const paymentTypeLabel = (type) => {
    if (type === "DC") return "DEBE";
    if (type === "DY") return "YAPE";
    if (type === "HC") return "HABER";
    return "EFECTIVO";
};

export function CollectionSheetForm({
    setCashFlow,
    cashFlow,
    cashFlowSet,
    setCashFlowSet,
    getAllSalesBySeller,
    isModalOpen,
    setIsModalOpen,
}) {
    const { auth } = useContext(ContextAuth);

    useEffect(() => {
        if (auth && cashFlow.userID === 0) {
            setCashFlow({ ...cashFlow, userID: auth.data.user_id });
        }
    }, [auth, cashFlow, setCashFlow]);

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        document.body.style.overflow = isModalOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [isModalOpen]);

    const closeModal = () => setIsModalOpen(false);

    function handleChange(e) {
        if (e.target.name === "total") {
            const formattedValue = e.target.value.replace(/[^0-9.]/g, "").slice(0, 9);
            setCashFlow({ ...cashFlow, total: formattedValue });
        } else {
            setCashFlow({ ...cashFlow, [e.target.name]: e.target.value });
        }
    }

    const handleSave = (e) => {
        e.preventDefault();
        if (Number(cashFlow.operationID) === 0) { toast.warning("No hay operación vinculada."); return; }
        if (Number(cashFlow.total) === 0) { toast.warning("No hay monto."); return; }

        savePayment(cashFlow).then(async (res) => {
            setCashFlowSet(res.cashFlowSet);
            getAllSalesBySeller();
        });
        closeModal();
    };

    const removeAndGetPayments = async (cashFlowID) => {
        try {
            const response = await removePayment({ cashFlowID });
            setCashFlowSet(response);
            getAllSalesBySeller();
        } catch (error) { throw error; }
    };

    const inputClass =
        "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
    const labelClass =
        "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";

    if (!isModalOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            aria-modal="true"
            role="dialog"
            aria-labelledby="collectSheetModalLabel"
        >
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />

            {/* Modal panel */}
            <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 bg-blue-600 dark:bg-blue-700 flex-shrink-0">
                    <h5 className="text-base font-semibold text-white flex items-center gap-2" id="collectSheetModalLabel">
                        <i className="fas fa-credit-card"></i>
                        Efectuar Pago
                    </h5>
                    <button
                        type="button"
                        onClick={closeModal}
                        className="text-white/80 hover:text-white transition-colors p-1 rounded"
                        aria-label="Cerrar"
                    >
                        <i className="fas fa-times text-lg"></i>
                    </button>
                </div>

                {/* Body — scrollable */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 bg-white dark:bg-gray-800">

                    {/* Payment form */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                            <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                <i className="fas fa-plus-circle text-blue-500"></i>
                                Agregar nuevo pago
                            </h6>
                        </div>
                        <div className="px-4 py-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">

                                {/* Tipo */}
                                <div>
                                    <label htmlFor="wayPayType" className={labelClass}>
                                        <i className="fas fa-credit-card mr-1"></i>Tipo de pago
                                    </label>
                                    <select
                                        id="wayPayType"
                                        name="type"
                                        value={cashFlow.type}
                                        onChange={handleChange}
                                        className={inputClass}
                                    >
                                        <option value="E">EFECTIVO</option>
                                        <option value="DY">YAPE</option>
                                    </select>
                                </div>

                                {/* Descripción */}
                                <div>
                                    <label htmlFor="wayPayDescription" className={labelClass}>
                                        <i className="fas fa-comment mr-1"></i>Descripción
                                    </label>
                                    <input
                                        type="text"
                                        id="wayPayDescription"
                                        name="description"
                                        placeholder="Descripción del pago"
                                        value={cashFlow.description}
                                        onChange={handleChange}
                                        onFocus={(e) => e.target.select()}
                                        className={inputClass}
                                    />
                                </div>

                                {/* Fecha */}
                                <div>
                                    <label htmlFor="transactionDate1" className={labelClass}>
                                        <i className="fas fa-calendar mr-1"></i>Fecha
                                    </label>
                                    <input
                                        type="date"
                                        id="transactionDate1"
                                        name="transactionDate"
                                        value={cashFlow.transactionDate}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </div>

                                {/* Total */}
                                <div>
                                    <label htmlFor="wayPayTotal" className={labelClass}>
                                        <i className="fas fa-money-bill-wave mr-1"></i>Total
                                    </label>
                                    <input
                                        type="number"
                                        id="wayPayTotal"
                                        name="total"
                                        placeholder="Monto a pagar"
                                        value={cashFlow.total}
                                        onChange={handleChange}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        onFocus={(e) => e.target.select()}
                                        className={inputClass}
                                    />
                                </div>

                                {/* Submit */}
                                <div>
                                    <button
                                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        onClick={handleSave}
                                    >
                                        <i className="fas fa-plus"></i>
                                        Agregar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payments list */}
                    {cashFlowSet?.length > 0 && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                                <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                    <i className="fas fa-list text-blue-500"></i>
                                    Pagos registrados ({cashFlowSet.length})
                                </h6>
                            </div>

                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto bg-white dark:bg-gray-900">
                                <table className="min-w-full text-sm border-separate border-spacing-0">
                                    <thead>
                                        <tr>
                                            {["#", "Descripción", "Fecha", "Tipo", "Total", ""].map((h) => (
                                                <th key={h} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-center">
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cashFlowSet.map((item, c) => (
                                            <tr key={c} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                                <td className="px-3 py-2 text-xs text-center font-semibold text-blue-600 dark:text-blue-400 border-b border-gray-100 dark:border-gray-700">
                                                    {item.id}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 max-w-[200px] truncate" title={item.description}>
                                                    {item.description}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center text-gray-600 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                                    {item.transactionDate}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center border-b border-gray-100 dark:border-gray-700">
                                                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${paymentTypeBadge(item.type)}`}>
                                                        {paymentTypeLabel(item.type)}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-xs text-right font-semibold text-emerald-700 dark:text-emerald-400 border-b border-gray-100 dark:border-gray-700">
                                                    S/ {Number(item.total).toFixed(2)}
                                                </td>
                                                <td className="px-3 py-2 text-xs text-center border-b border-gray-100 dark:border-gray-700">
                                                    {item.type !== "HC" && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeAndGetPayments(item.id)}
                                                            className="inline-flex items-center gap-1 rounded-md border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs px-2 py-1 transition-colors"
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
                                {cashFlowSet.map((item, c) => (
                                    <div key={c} className="p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <p className="text-xs font-bold text-blue-600 dark:text-blue-400">#{item.id}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                                            </div>
                                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${paymentTypeBadge(item.type)}`}>
                                                {paymentTypeLabel(item.type)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                <i className="fas fa-calendar mr-1"></i>{item.transactionDate}
                                            </span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                                    S/ {Number(item.total).toFixed(2)}
                                                </span>
                                                {item.type !== "HC" && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAndGetPayments(item.id)}
                                                        className="inline-flex items-center gap-1 rounded-md border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs px-2 py-1 transition-colors"
                                                    >
                                                        <i className="fas fa-trash mr-1"></i>Eliminar
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Empty state */}
                    {(!cashFlowSet || cashFlowSet.length === 0) && (
                        <div className="py-10 text-center">
                            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 mb-3">
                                <i className="fas fa-receipt text-gray-400 dark:text-gray-500 text-xl"></i>
                            </div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No hay pagos registrados</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                Agregue un pago usando el formulario de arriba.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium px-4 py-2 transition-colors"
                    >
                        <i className="fas fa-times"></i>
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
}
